import React, { useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, Form, Input, InputNumber, Switch, Button, Select, message, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getExploreAudio, getAllExploreCategories } from '../../graphql/queries';
import { updateExploreAudio } from '../../graphql/mutation';
import './Explore.css';

const { TextArea } = Input;

const ExploreAudioDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const { loading: queryLoading, data } = useQuery(getExploreAudio, {
    variables: { id },
    skip: !id
  });

  const { data: categoriesData } = useQuery(getAllExploreCategories);
  const categories = categoriesData?.getAllExploreCategories || [];

  const [updateAudio, { loading: updateLoading }] = useMutation(updateExploreAudio, {
    onCompleted: () => {
      message.success('Audio updated successfully');
      navigate('/explore');
    },
    onError: (error) => message.error(error.message)
  });

  const audio = data?.getExploreAudio;

  useEffect(() => {
    if (audio) {
      form.setFieldsValue({
        name: audio.name,
        categoryId: audio.categoryId,
        audioUrl: audio.audioUrl,
        imageUrl: audio.imageUrl,
        prompt: audio.prompt,
        lyrics: audio.lyrics,
        order: audio.order,
        isActive: audio.isActive
      });
    }
  }, [audio, form]);

  const onFinish = (values) => {
    updateAudio({
      variables: {
        id,
        input: {
          name: values.name,
          categoryId: values.categoryId,
          audioUrl: values.audioUrl,
          imageUrl: values.imageUrl || null,
          prompt: values.prompt || null,
          lyrics: values.lyrics || null,
          order: values.order,
          isActive: values.isActive
        }
      }
    });
  };

  if (queryLoading) {
    return (
      <div className="explore-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="explore-container">
      <div className="page-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/explore')}
          style={{ marginBottom: 16 }}
        >
          Back to Explore
        </Button>
        <h1 className="page-title">Edit Explore Audio</h1>
        <p className="page-subtitle">Update audio details</p>
      </div>

      <Card className="explore-card" style={{ maxWidth: 700 }}>
        {audio?.imageUrl && (
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            <img
              src={audio.imageUrl}
              alt={audio.name}
              style={{ maxWidth: 200, maxHeight: 200, borderRadius: 12, objectFit: 'cover' }}
            />
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter audio name' }]}
          >
            <Input placeholder="Audio name" />
          </Form.Item>

          <Form.Item
            name="categoryId"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select category">
              {categories.map(cat => (
                <Select.Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="audioUrl"
            label="Audio URL"
            rules={[
              { required: true, message: 'Please enter audio URL' },
              { type: 'url', message: 'Please enter a valid URL' }
            ]}
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item
            name="imageUrl"
            label="Image URL"
            rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item
            name="prompt"
            label="Prompt"
          >
            <TextArea rows={3} placeholder="Audio prompt" />
          </Form.Item>

          <Form.Item
            name="lyrics"
            label="Lyrics"
          >
            <TextArea rows={4} placeholder="Audio lyrics" />
          </Form.Item>

          <Form.Item
            name="order"
            label="Order"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Display order" />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Active"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={updateLoading}>
              Update Audio
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ExploreAudioDetail;
